import type { HeadingKeywords } from 'generated/graphql';

/**
 * RP-6004 (M1) — the single canonical leading-`Label:` detector. It serves two
 * distinct purposes, selected by whether a `labelSet` is supplied:
 *
 * 1. Heading determination (rendering + section split) — callers pass the
 *    recognised-label `labelSet`. ONLY recognised section keywords are headings.
 *    A non-keyword label (e.g. an anatomical subsection like `Stomach:`) is NOT
 *    a heading: it is not styled as one and does not become a top-level
 *    `field`/section on save. (Requirement change 2026-08-18 — previously short
 *    unlisted labels were also treated as headings.)
 *
 * 2. Field naming — callers pass NO `labelSet` (e.g. `fieldNaming`). The matcher
 *    falls back to the anchored pattern for any leading first-sentence `Label:`
 *    run (keyword or not), bounded by `MAX_UNLISTED_HEADING_LABEL_CHARS`, so a
 *    field following a subsection is still named after it. Ratios (`2:1`), times
 *    (`14:30`) and colons after a sentence boundary (`hello. Stomach:`) never match.
 *
 * The label set is the backend's own section-label list, delivered to the client
 * via `useHeadingKeywords` (GraphQL `headingKeywords` → backend
 * `/ugc/heading-keywords` → `REPORT_SECTION_VARIANT_LABEL_SYNONYMS`). Tech spec §1.
 */

// Anchored: optional leading whitespace, then a label run, then a colon. The label matches EITHER:
//   (a) a letter-led run — letters, spaces, and common heading punctuation (`, & / ' -`). It
//       deliberately EXCLUDES digits and sentence-ending `. ? !`, so the colon is only reachable
//       inside the FIRST sentence; or
//   (b) a vertebral/spine level — `L1`, `L1-L2`, `L1-2`, `T12-L1`, `L5-S1`, `C5-C6`, `S1`, … — the
//       ONLY way a digit-bearing label matches, so anatomical subsection labels are recognised.
// One capture group (group 2) holds the label body from whichever branch, so extraction is unchanged.
//   "KIDNEYS, URETERS, AND BLADDER:"    -> matches (a) (heading only if a keyword; else a field-name candidate)
//   "L1-L2:", "T12-L1:", "S1:"          -> matches (b) (spine subsection labels)
//   "hello. KIDNEYS, ...:"              -> no match (the "." before the colon breaks branch (a))
//   "2:1", "14:30", "12:30", "at 12:30" -> no match (branch (a) needs a leading letter; branch (b)
//                                          needs a leading C/T/L/S immediately followed by a digit)
const HEADING_LIKE_PREFIX =
  /^(\s*)((?:[A-Za-z][A-Za-z ,&/'-]*?)|(?:[CTLS]\d{1,2}(?:\s*[-/–]\s*[CTLS]?\d{1,2})*)):/;

// Generous backstop so a runaway first sentence that merely ends in a colon isn't treated as a
// heading. Recognised keywords bypass it; unlisted labels (e.g. anatomical subsections) must fit.
// Tunable.
export const MAX_UNLISTED_HEADING_LABEL_CHARS = 60;

export interface HeadingLikeMatch {
  /** Offset of the first label character within the text. */
  start: number;
  /** Offset just past the trailing colon. */
  end: number;
  /** The matched label including the trailing colon, e.g. `Impression:`. */
  label: string;
}

/** Normalise a label for set membership: drop the colon, collapse spaces, lower-case. */
export const normalizeHeadingLabel = (label: string): string =>
  label.trim().replace(/:$/, '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Flatten the `HeadingKeywords` object (exam/history/technique/findings/
 * comparison/impressions) into a lower-cased set — the frontend mirror of the
 * backend's `get_heading_keywords_list()`.
 */
export const buildHeadingLabelSet = (headingKeywords?: HeadingKeywords | null): Set<string> => {
  const set = new Set<string>();
  if (headingKeywords == null) return set;
  for (const value of Object.values(headingKeywords)) {
    if (!Array.isArray(value)) continue; // skip __typename
    for (const keyword of value) {
      if (typeof keyword === 'string') set.add(normalizeHeadingLabel(keyword));
    }
  }
  return set;
};

export const matchHeadingLikePrefix = (
  text: string,
  labelSet?: Set<string>
): HeadingLikeMatch | null => {
  if (typeof text !== 'string') return null;

  const match = text.match(HEADING_LIKE_PREFIX);
  if (match == null) return null;

  const leadingWhitespace = match[1] ?? '';
  const labelBody = match[2] ?? '';
  const start = leadingWhitespace.length;
  const end = start + labelBody.length + 1; // +1 for the colon
  const label = text.slice(start, end);

  const normalized = normalizeHeadingLabel(label);

  // Heading determination (rendering + section split): when a recognised-label set is supplied,
  // ONLY those keywords are headings. A non-keyword label (e.g. `Stomach:`) is not a heading — not
  // styled, not split into a section. A PRESENT-BUT-EMPTY set also takes this branch and matches
  // nothing, so a momentarily/erroneously empty keyword list can never fall through to broad
  // matching and over-decorate every `Label:` line. Only a truly absent (undefined) set is broad.
  if (labelSet != null) {
    return labelSet.has(normalized) ? { start, end, label } : null;
  }

  // Field-naming path (no label set at all): any leading first-sentence `Label:` run, capped so a
  // runaway sentence ending in a colon isn't captured.
  if (normalized.length > MAX_UNLISTED_HEADING_LABEL_CHARS) return null;
  return { start, end, label };
};

export const isHeadingLikeText = (text: string, labelSet?: Set<string>): boolean =>
  matchHeadingLikePrefix(text, labelSet) != null;
